/**
 * The build gate: whether these two peers run the same simulation, and what to
 * say when they do not.
 *
 * ## Why a duel needs this and the protocol version was not it
 *
 * Input-delay lockstep transmits *sticks*, never state. Both machines then
 * compute the whole fight from them, so the precondition for a duel existing at
 * all is that the two machines compute the same fight from the same sticks.
 * `NET_PROTOCOL_VERSION` does not answer that question — it is a hand-
 * maintained integer that guards the *wire*, and it sat at 1 from the duel's
 * first commit through 57 commits to `packages/engine`'s flight and combat
 * code. Any one of those (a drag coefficient recalibrated against DOSBox, a new
 * manoeuvre in the AI's table, the airship RNG stream) makes two builds fly the
 * same stick differently while both frames on the wire stay byte-identical.
 *
 * Measured against the real runners, that is what the reported bug looks like:
 * peers whose encounters differ at construction diverge on **tick 1** and are
 * only *told* at **tick 60**, the first scheduled hash — one second of flying a
 * fight that only exists on your own screen, then "the two simulations
 * diverged" with no cause named. This week's service-worker incidents make it
 * an ordinary thing to happen: one player's browser holding a worker a deploy
 * behind the other's is precisely two different builds in one room.
 *
 * ## Why the identity is `engineFingerprint()`
 *
 * It is measured, not declared (`packages/engine/src/replay/engine-fingerprint.ts`):
 * the build flies a fixed stick through a fixed flight and a fixed fight and
 * hashes where it lands, so it moves when the model moves whether or not anyone
 * remembered to bump a constant. For a *tape* that fingerprint is documented as
 * "not a cross-platform identity" — floating point is pinned to a hardware and
 * browser family — and for a *duel* that caveat is not a weakness but the
 * point: two machines that fingerprint differently are two machines that would
 * not step in lockstep, whatever build they are running. Refusing is the
 * correct answer to both causes, and the message names the likely one without
 * claiming to know which it was.
 *
 * ## What this module is not
 *
 * It has no opinion about sockets, and it does not call `engineFingerprint()`
 * itself — the caller supplies both identities, so the whole gate is a pure
 * function that a test can drive with two strings.
 */

/** Who a peer is, in the only sense a lockstep duel cares about. */
export interface BuildIdentity {
  /** `NET_PROTOCOL_VERSION` — guards the wire. */
  readonly protocol: number;
  /** `engineFingerprint()` — guards the simulation. */
  readonly engine: string;
}

export const BUILD_VERDICTS = [
  /** Nothing has been heard from the peer yet. Not an answer — a duel must not start on it. */
  "unconfirmed",
  /** Same wire and same simulation. The only verdict a duel may be flown on. */
  "same",
  /** Either half differs, or the peer said something this build cannot read. */
  "different",
] as const;

export type BuildVerdict = (typeof BUILD_VERDICTS)[number];

export function buildVerdict(mine: BuildIdentity, theirs: BuildIdentity | null): BuildVerdict {
  if (theirs === null) return "unconfirmed";
  return theirs.protocol === mine.protocol && theirs.engine === mine.engine ? "same" : "different";
}

/** How much of a fingerprint is worth putting in front of a player: enough to compare, not enough to read out. */
export const BUILD_STAMP_CHARS = 8;

export function buildStamp(engine: string): string {
  return engine.slice(0, BUILD_STAMP_CHARS);
}

/**
 * The line the lobby prints instead of desyncing. It names the fact, the
 * consequence, the action, and the two stamps a bug report would need — and it
 * stops short of claiming the cause is certainly a stale deploy, because a peer
 * on hardware that computes differently produces the same verdict.
 *
 * `theirs` is `null` when the peer never managed a readable `hello` at all,
 * which is what an older protocol looks like from here.
 */
export function buildMismatchNote(mine: BuildIdentity, theirs: BuildIdentity | null): string {
  const his = theirs === null ? "unreadable" : buildStamp(theirs.engine);
  return (
    "Your opponent is flying a different build — these two games would not fly the same fight. " +
    "Reload the page, both of you, and try again. " +
    `(Yours ${buildStamp(mine.engine)}, his ${his}.)`
  );
}
